import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { cookieManager } from '../../utils/cookieManager.js';
import { CalendarClock, FileText, CheckCircle, Globe, Info, Shield, Sparkles } from 'lucide-react';
import Footer from '../shared/Footer';
import VisaAssistantSection from '../visa-assistant/VisaAssistantSection';

// 小工具：设置页面标题
const usePageTitle = (title) => {
  React.useEffect(() => {
    if (title) document.title = title;
  }, [title]);
};

// 美国签证专题首页
const UsVisaHome = () => {
  usePageTitle('旅报报·美国签证助手 | DS-160填写 · 面谈预约 · EVUS · 状态查询');
  const navigate = useNavigate();
  const isLoggedIn = cookieManager.getAuthInfo().isLoggedIn;

  // 轮播状态管理
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);

  const gotoDS160 = () => {
    if (isLoggedIn) navigate('/home'); else navigate('/auth/login');
  };

  const gotoEVUS = () => {
    if (isLoggedIn) navigate('/evus/enroll'); else navigate('/auth/login');
  };

  // 幻灯片数据配置
  const slides = [
    {
      id: 'ds160',
      title: 'DS-160 自助填写',
      price: '¥100',
      comparePrice: '代填服务通常 ¥300-500',
      features: [
        '自己填写，隐私有保障',
        '智能校验，减少错误',
        '云端保存，随时继续',
        '无限修改，直到满意'
      ],
      buttonText: '立即开始填写',
      onClick: gotoDS160
    },
    {
      id: 'evus',
      title: 'EVUS 签证更新',
      price: '250积分',
      comparePrice: '包含官方$30收费 + 代办服务费',
      features: [
        '快速更新，10分钟完成',
        '中文界面，操作简单',
        '自动提醒，避免过期',
        '安全可靠，信息加密'
      ],
      buttonText: '立即更新EVUS',
      onClick: gotoEVUS
    }
  ];

  // 自动轮播逻辑
  React.useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isPaused, slides.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* 顶部导航 */}
      <header className="sticky top-0 z-40 backdrop-blur bg-white/60 border-b border-white/30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="旅报报logo" className="w-8 h-8" />
            <div className="font-extrabold text-gray-900">旅报报·美国签证助手</div>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="https://lvbaobao.space/"
              className="inline-flex items-center px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:border-blue-500 hover:text-blue-600 hover:bg-white transition-all"
            >
              <span className="sm:hidden">返回主站</span>
              <span className="hidden sm:inline">返回 lvbaobao.space</span>
            </a>
            <nav className="hidden md:flex items-center gap-6 text-sm text-gray-700">
              <a href="#features" className="hover:text-gray-900">产品能力</a>
              <a href="#flow" className="hover:text-gray-900">办理流程</a>
              <a href="#compare" className="hover:text-gray-900">对比与优势</a>
              <a href="#faq" className="hover:text-gray-900">FAQ</a>
              {isLoggedIn ? (
                <Link to="/home" className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium hover:from-blue-700 hover:to-indigo-700 transition-all">
                  个人中心
                </Link>
              ) : (
                <Link to="/auth/login" className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-medium hover:border-blue-500 hover:text-blue-600 transition-all">
                  登录
                </Link>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-indigo-400/20 to-pink-600/20 rounded-full blur-3xl"></div>

        <div className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-medium mb-4">
              <Sparkles className="w-4 h-4 mr-2" /> AI 美国签证助手
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent leading-tight">
              美国签证，一步到位
            </h1>
            <p className="mt-4 text-gray-700 text-lg md:text-xl">
              自助签证不求人。AI 驱动，从 DS‑160 到面谈与后续管理，全流程更快、更准、更省心。
            </p>

            {/* 核心卖点标签 */}
            <div className="mt-6 flex flex-wrap gap-3">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium">
                <Shield className="w-4 h-4 mr-2" />
                隐私保护：自己填写，信息不外传
              </div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-100 text-amber-800 text-sm font-medium">
                <span className="mr-2">💰</span>
                超值价格：仅需 ¥100，省心省钱
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              {/* 主按钮：DS160填写 */}
              <button
                onClick={gotoDS160}
                className="group inline-flex items-center justify-center px-6 py-4 rounded-2xl text-gray-900 bg-white border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold"
              >
                <FileText className="w-5 h-5 mr-2" />
                DS160填写
              </button>

              {/* 次要按钮：面谈预约 */}
              <Link
                to="/interview"
                className="group inline-flex items-center justify-center px-6 py-4 rounded-2xl bg-white/90 backdrop-blur-sm text-gray-900 border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg hover:scale-105 transition-all duration-300 font-medium"
              >
                <CalendarClock className="w-5 h-5 mr-2 text-blue-600" />
                面谈预约
              </Link>

              {/* 次要按钮：EVUS 管理 */}
              <Link
                to="/evus"
                className="group inline-flex items-center justify-center px-6 py-4 rounded-2xl bg-white/90 backdrop-blur-sm text-gray-900 border-2 border-gray-200 hover:border-indigo-400 hover:shadow-lg hover:scale-105 transition-all duration-300 font-medium"
              >
                <FileText className="w-5 h-5 mr-2 text-indigo-600" />
                EVUS 管理
              </Link>

              {/* 次要按钮：签证状态检查 */}
              <Link
                to="/status"
                className="group inline-flex items-center justify-center px-6 py-4 rounded-2xl bg-white/90 backdrop-blur-sm text-gray-900 border-2 border-gray-200 hover:border-purple-400 hover:shadow-lg hover:scale-105 transition-all duration-300 font-medium"
              >
                <CheckCircle className="w-5 h-5 mr-2 text-purple-600" />
                签证状态检查
              </Link>
            </div>
          </div>
          {/* 轮播卡片容器 */}
          <div
            className="relative overflow-hidden"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* 幻灯片轨道 */}
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide) => (
                <div key={slide.id} className="w-full flex-shrink-0">
                  <div className="bg-white rounded-3xl border-2 border-blue-200 shadow-xl p-8">
                    <div className="text-center mb-6">
                      <div className="text-gray-500 text-sm mb-2">{slide.title}</div>
                      <div className="text-5xl font-extrabold text-blue-600">{slide.price}</div>
                      <div className="text-gray-400 text-sm mt-2 line-through">{slide.comparePrice}</div>
                    </div>
                    <ul className="space-y-3 text-gray-700">
                      {slide.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={slide.onClick}
                      className="w-full mt-6 px-6 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
                    >
                      {slide.buttonText}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* 指示器圆点 */}
            <div className="flex justify-center gap-2 mt-6">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentSlide
                      ? 'bg-blue-600 w-6 h-2'
                      : 'bg-gray-300 w-2 h-2 hover:bg-gray-400'
                  }`}
                  aria-label={`切换到幻灯片 ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 🆕 新增：B1/B2签证材料与流程助手 */}
      <VisaAssistantSection />

      {/* 为什么选择自助填写 */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent mb-2">
              为什么选择自助填写？
            </h2>
            <p className="text-gray-600 text-lg">对比传统代填服务，优势一目了然</p>
          </div>

          {/* 对比表格 */}
          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="p-4 text-left bg-gray-100 rounded-tl-xl">对比项</th>
                  <th className="p-4 text-center bg-red-50 text-red-800">传统代填服务</th>
                  <th className="p-4 text-center bg-green-50 text-green-800 rounded-tr-xl">旅报报自助填写</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-200">
                  <td className="p-4 font-medium">💰 价格</td>
                  <td className="p-4 text-center text-red-600">¥300-500</td>
                  <td className="p-4 text-center text-green-600 font-bold">¥100</td>
                </tr>
                <tr className="border-t border-gray-200 bg-gray-50">
                  <td className="p-4 font-medium">🔒 隐私安全</td>
                  <td className="p-4 text-center text-red-600">需提交全部个人信息给陌生人</td>
                  <td className="p-4 text-center text-green-600">自己填写，信息不外传</td>
                </tr>
                <tr className="border-t border-gray-200">
                  <td className="p-4 font-medium">⏱️ 时效</td>
                  <td className="p-4 text-center text-gray-600">等待代填人员处理</td>
                  <td className="p-4 text-center text-green-600">即时填写，随时暂停继续</td>
                </tr>
                <tr className="border-t border-gray-200 bg-gray-50">
                  <td className="p-4 font-medium">✅ 准确性</td>
                  <td className="p-4 text-center text-gray-600">依赖代填人员理解</td>
                  <td className="p-4 text-center text-green-600">自己最了解自己的情况</td>
                </tr>
                <tr className="border-t border-gray-200">
                  <td className="p-4 font-medium rounded-bl-xl">✏️ 修改</td>
                  <td className="p-4 text-center text-gray-600">需联系代填人员，可能额外收费</td>
                  <td className="p-4 text-center text-green-600 rounded-br-xl">随时自主修改，无额外费用</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* 隐私风险警示 */}
          <div className="bg-gradient-to-r from-rose-50 to-red-50 border-2 border-rose-300 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="text-3xl">⚠️</div>
              <div>
                <h3 className="font-bold text-rose-800 text-lg mb-2">代填服务的隐私风险</h3>
                <p className="text-rose-700 text-sm leading-relaxed">
                  DS-160 表格包含您的<strong>护照信息、家庭住址、工作单位、收入情况、旅行记录</strong>等大量敏感个人信息。
                  将这些信息提交给代填服务，意味着您的隐私完全暴露给陌生人。一旦信息泄露，可能面临：
                </p>
                <ul className="mt-2 text-rose-700 text-sm space-y-1">
                  <li>• 电信诈骗、身份盗用</li>
                  <li>• 个人信息被转卖</li>
                  <li>• 无法追溯和删除</li>
                </ul>
                <p className="mt-3 text-rose-800 font-medium">
                  选择自助填写，您的信息只在您自己手中，安全有保障。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 产品能力 */}
      <section id="features" className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent mb-2">产品与能力</h2>
            <p className="text-gray-600 text-lg">围绕真实办理流程设计，减少错误、提升效率</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {title:'智能表单填写', desc:'分步引导 + 规则校验 + 关键字段二次确认，降低误填率。', icon: FileText},
              {title:'材料与照片检查', desc:'自动校验照片尺寸/背景/清晰度，提供示例与修正建议。', icon: CheckCircle},
              {title:'进度追踪与提醒', desc:'关键节点清晰可见，准时提醒，不遗漏任何步骤。', icon: CalendarClock},
              {title:'云端保存，随时续写', desc:'自动保存，跨设备继续；支持数据导出与删除。', icon: Globe},
              {title:'安全与隐私', desc:'全站 HTTPS 传输；最小化收集；不经第三方交易平台中转。', icon: Shield},
              {title:'专题指南', desc:'覆盖常见问题与办理要点，新手也能稳步完成。', icon: Info},
            ].map((f, i)=> (
              <div key={i} className="group bg-white rounded-2xl p-6 border-2 border-gray-100 shadow-md hover:shadow-xl hover:border-blue-200 transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 group-hover:from-blue-200 group-hover:to-indigo-200 transition-colors">
                    <f.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="font-bold text-gray-900 text-lg">{f.title}</div>
                </div>
                <div className="text-gray-600 text-sm leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 办理流程 */}
      <section id="flow" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent mb-2">办理流程</h2>
            <p className="text-gray-600 text-lg">5步完成，轻松搞定签证申请</p>
          </div>
          <ol className="flex flex-wrap gap-4 justify-center">
            {['填写 DS‑160','缴费与面谈预约','准备材料','面谈','审理与出签 / EVUS（如需）'].map((s, i)=>(
              <li key={i} className="group bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200 px-6 py-4 text-gray-800 flex items-center gap-3 hover:from-blue-100 hover:to-indigo-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <span className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-bold group-hover:shadow-lg transition-shadow">{i+1}</span>
                <span className="font-medium">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 对比与优势 */}
      <section id="compare" className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent mb-2">对比与优势</h2>
            <p className="text-gray-600 text-lg">为什么选择旅报报</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: '更高准确性', desc: '规则校验 + 二次确认，避免关键信息遗漏与格式错误。', icon: CheckCircle, color: 'from-green-100 to-emerald-100', textColor: 'text-green-600' },
              { title: '更高效率', desc: '流程拆解与自动保存，随时继续不丢失。', icon: CalendarClock, color: 'from-blue-100 to-indigo-100', textColor: 'text-blue-600' },
              { title: '更透明与可控', desc: '全程自己掌控，所见即所得，费用结构清晰。', icon: Shield, color: 'from-purple-100 to-pink-100', textColor: 'text-purple-600' },
            ].map((item, i) => (
              <div key={i} className="group bg-white rounded-2xl p-7 border-2 border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className={`inline-flex p-3 rounded-lg bg-gradient-to-br ${item.color} mb-4`}>
                  <item.icon className={`w-6 h-6 ${item.textColor}`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">常见问题</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <details className="bg-gray-50 rounded-xl border border-gray-200 p-4"><summary className="font-semibold">填写 DS‑160 需要哪些信息？</summary><p className="mt-2 text-gray-700">护照、个人信息、旅行计划、工作/教育经历等；系统会分步提示与校验。</p></details>
            <details className="bg-gray-50 rounded-xl border border-gray-200 p-4"><summary className="font-semibold">照片有什么要求？</summary><p className="mt-2 text-gray-700">白色背景、近6个月内拍摄、尺寸与像素符合官方标准；系统会给出详细说明与示例。</p></details>
            <details className="bg-gray-50 rounded-xl border border-gray-200 p-4"><summary className="font-semibold">如何预约面谈？</summary><p className="mt-2 text-gray-700">完成 DS‑160 后，在对应国家/地区的 USTravelDocs/CGI 官网创建账户、缴费并选择时间；详见“面谈预约”。</p></details>
            <details className="bg-gray-50 rounded-xl border border-gray-200 p-4"><summary className="font-semibold">是否支持移动端？</summary><p className="mt-2 text-gray-700">支持主流浏览器的移动端访问。</p></details>
          </div>
        </div>
      </section>

      {/* 页脚 */}
      <Footer variant="full" showCopyright={true} />
    </div>
  );
};

// 信息页（MVP：指南 + 官方入口）
const InfoPage = ({ type }) => {
  const isLoggedIn = cookieManager.getAuthInfo().isLoggedIn;
  const titles = {
    interview: '面谈预约 | 旅报报·美国签证助手',
    evus: 'EVUS 填写 | 旅报报·美国签证助手',
    status: '签证状态检查 | 旅报报·美国签证助手',
  };
  usePageTitle(titles[type] || '旅报报·美国签证助手');

  const blocks = {
    interview: {
      h2: '面谈预约',
      desc: '完成 DS‑160 后，前往官方预约系统创建账户、缴费并选择可用时间。不同国家/地区入口不同，请以所在地区为准。',
      checklist: ['DS‑160 确认页（带条码）','护照原件与信息页复印件','照片（符合规格）','签证费用（根据地区与类型）','其他（根据个人情况与签证类型）'],
      steps: ['在官方预约网站注册/登录账户','录入 DS‑160 号码与个人信息','缴纳签证费并保留凭证','选择签证类别与可用时间','确认预约并保存预约确认页'],
      buttons: [
        {
          text: '自动注册预约账户',
          href: isLoggedIn ? '/auth/auto-register' : '/auth/login',
          type: 'internal',
          primary: true
        },
        {
          text: '前往官方预约网站',
          href: 'https://www.usvisascheduling.com/zh-CN/SignIn',
          type: 'external',
          primary: false
        },
        {
          text: '返回首页',
          href: '/',
          type: 'internal',
          primary: false
        }
      ]
    },
    evus: {
      h2: 'EVUS 管理',
      // 精简描述，避免与下方三块主题重复
      desc: 'EVUS 登记适用范围、所需材料与与签证的关系说明如下。',
      // 该页改为三块主题说明，不再展示准备清单与操作步骤，置为空以避免重复
      checklist: [],
      steps: [],
      additionalSections: [
        {
          title: '谁需要注册？',
          items: [
            '您是中国公民。',
            '您持有效中国护照。',
            '您赴美的目的是短期商务或旅游。',
            '您必须登记一个新的EVUS。'
          ]
        },
        {
          title: '需要哪些材料来注册？',
          items: [
            '有效的中国护照。',
            '有效期为10年的美国签证。',
            '签证的级别属于以下其中一项：B1、B2 或 B1/B2。',
            '您的联系信息，包括您在美国目的地的地址。'
          ]
        },
        {
          title: 'EVUS和签证之间的区别是什么？',
          items: [
            'EVUS 登记不是签证，不能替代签证。',
            '计划前往美国访问的旅客必须持有有效的 B1、B2 或 B1/B2 访客签证；中华人民共和国公民/国民仅持有有效的十年 B1、B2 或 B1/B2 签证，需在行前完成 EVUS 登记。',
            '完成 EVUS 登记并抵达机场的旅客可以前往美国（是否准入仍由边检依照法律最终决定）。',
            '其他任何非 B1、B2 或 B1/B2 且为十年有效期类型的签证，不需要申请 EVUS 登记。'
          ]
        }
      ],
          buttons: [
            {
              text: 'EVUS自助办理',
              href: '/evus/enroll',
              type: 'internal',
              primary: false
            },
            {
              text: 'EVUS状态查询',
              href: '/evus/query',
              type: 'internal',
              primary: false
            },
            {
              text: 'EVUS官网',
              href: 'https://www.evus.gov/',
              type: 'external',
              primary: false
            }
      ]
    },
    status: {
      h2: '签证状态检查',
      desc: '可在美国国务院 CEAC 官方网站查询非移民签证（NIV）状态。通常需要选择签证受理地点并输入 DS‑160 确认号等。',
      checklist: ['选择受理地点（使领馆）','准备 DS‑160 确认号','完成人机验证'],
      steps: ['打开 CEAC 状态查询页面（NIV）','选择受理地点（使领馆）','输入 DS‑160 确认号','完成人机验证并提交查询'],
      linkText: '前往 CEAC 状态查询',
      linkHref: 'https://ceac.state.gov/CEACStatTracker/Status.aspx?App=NIV',
    },
  };

  const data = blocks[type] || blocks.interview;

  return (
      <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">{data.h2}</h1>
        <p className="text-gray-700 mb-6">{data.desc}</p>
        {type === 'evus' && (
          <div className="mb-6 p-4 rounded-2xl bg-amber-50 text-amber-900 border border-amber-200">
            <strong>隐私提示：</strong>本服务后台不保存任何美国EVUS登记记录，请在完成后自行保存登记号与确认页截图。
          </div>
        )}
        {(((data.checklist?.length ?? 0) > 0) || ((data.steps?.length ?? 0) > 0)) && (
          <div className="grid md:grid-cols-2 gap-4">
            {data.checklist?.length ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="font-bold text-gray-900 mb-2">准备清单</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {data.checklist.map((c, i)=> <li key={i}>{c}</li>)}
                </ul>
              </div>
            ) : null}
            {data.steps?.length ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="font-bold text-gray-900 mb-2">操作步骤</h3>
                <ol className="list-decimal list-inside text-gray-700 space-y-1">
                  {data.steps.map((s, i)=> <li key={i}>{s}</li>)}
                </ol>
              </div>
            ) : null}
          </div>
        )}
        {/* EVUS额外信息模块 */}
        {data.additionalSections && (
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.additionalSections.map((section, i) => {
              const Icon = i === 0 ? Shield : i === 1 ? Info : Globe;
              const iconColor =
                i === 0 ? 'text-indigo-600' : i === 1 ? 'text-blue-600' : 'text-emerald-600';
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Icon className={`w-5 h-5 ${iconColor} mr-2`} />
                    {section.title}
                  </h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm leading-6">
                    {section.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-6 flex gap-3 flex-wrap">
          {data.buttons ? (
            // 新的 buttons 配置模式
            <>
              {data.buttons.map((btn, idx) => {
                const buttonClass = btn.primary
                  ? "group inline-flex items-center justify-center px-6 py-4 rounded-2xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg hover:scale-105 transition-all duration-300 font-medium"
                  : "group inline-flex items-center justify-center px-6 py-4 rounded-2xl bg-white/90 backdrop-blur-sm text-gray-900 border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg hover:scale-105 transition-all duration-300 font-medium";

                return btn.type === 'external' ? (
                  <a
                    key={idx}
                    className={buttonClass}
                    href={btn.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {btn.text}
                  </a>
                ) : (
                  <Link
                    key={idx}
                    className={buttonClass}
                    to={btn.href}
                  >
                    {btn.text}
                  </Link>
                );
              })}
            </>
          ) : (
            // 兼容旧的 linkText/linkHref 模式
            <>
              <a className="px-5 py-3 rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700" href={data.linkHref} target="_blank" rel="noopener noreferrer">{data.linkText}</a>
              <Link to="/" className="px-5 py-3 rounded-xl bg-white border border-gray-200">返回首页</Link>
            </>
          )}
        </div>
        <div className="mt-10 text-gray-500 text-sm">说明：以上链接与信息以官方网站为准，建议错峰访问并确保网络稳定。</div>
        <div className="mt-6">
          <Footer variant="simple" showCopyright={false} />
        </div>
      </div>
    </div>
  );
};

// 让 <UsVisaHome.Page type="..." /> 可用
UsVisaHome.Page = InfoPage;

export default UsVisaHome;
